import {
  CheckCircleIcon,
  ChevronDownIcon,
  ClockIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  PhoneIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api from '../../services/api';

const PaymentManagement = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedOverdue, setSelectedOverdue] = useState(null);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showPaymentActionModal, setShowPaymentActionModal] = useState(false);
  const [selectedPaymentAction, setSelectedPaymentAction] = useState(null);
  const [showSmsPaymentModal, setShowSmsPaymentModal] = useState(false);
  const [showSmsFormatModal, setShowSmsFormatModal] = useState(false);
  const [showManualMatchModal, setShowManualMatchModal] = useState({ show: false, smsRecord: null });
  const [showBillingSearchModal, setShowBillingSearchModal] = useState({ show: false, smsId: null, matches: [], smsRecord: null });
  const [showBillingConfirmationModal, setShowBillingConfirmationModal] = useState({ show: false, smsId: null, billingStatement: null, action: null });
  
  // 검색 및 필터 상태
  const [searchFilters, setSearchFilters] = useState({
    status: 'all',
    billing_month: '',
    building_id: '',
    tenant_name: '',
    property_owner_id: ''
  });

  // 대시보드 데이터
  const { data: dashboardData, isLoading: dashboardLoading } = useQuery({
    queryKey: ['payment-dashboard'],
    queryFn: () => api.get('/payment/dashboard')
  });


  // SMS 입금내역 필터 상태
  const [smsFilters, setSmsFilters] = useState({
    bank_name: '',
    status: 'all',
    date_from: (() => {
      const date = new Date();
      date.setDate(date.getDate() - 7); // 지난 7일
      return date.toISOString().split('T')[0];
    })(),
    date_to: new Date().toISOString().split('T')[0], // 오늘
    property_owner_id: ''
  });

  // SMS 검증 관련 상태
  const [smsVerificationView, setSmsVerificationView] = useState('summary'); // 'summary', 'daily', 'unmatched'
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSmsIds, setSelectedSmsIds] = useState([]);
  
  // 일별 SMS 요약 조회
  const { data: dailySummaryData, refetch: refetchDailySummary } = useQuery({
    queryKey: ['sms-daily-summary', smsFilters.date_from, smsFilters.date_to],
    queryFn: () => {
      const params = new URLSearchParams();
      if (smsFilters.date_from) params.append('date_from', smsFilters.date_from);
      if (smsFilters.date_to) params.append('date_to', smsFilters.date_to);
      return api.get(`/sms-verification/daily-summary?${params.toString()}`);
    },
    enabled: smsVerificationView === 'summary' || smsVerificationView === 'daily'
  });

  // 특정 일자 SMS 상세 조회
  const { data: dailyDetailsData, refetch: refetchDailyDetails } = useQuery({
    queryKey: ['sms-daily-details', selectedDate, smsFilters.property_owner_id],
    queryFn: () => {
      const params = new URLSearchParams();
      if (smsFilters.property_owner_id) {
        params.append('property_owner_id', smsFilters.property_owner_id);
      }
      const queryString = params.toString();
      return api.get(`/sms-verification/daily-details/${selectedDate}${queryString ? '?' + queryString : ''}`);
    },
    enabled: !!selectedDate && smsVerificationView === 'daily'
  });

  // Unmatched SMS 조회
  const { data: unmatchedSmsData, refetch: refetchUnmatched } = useQuery({
    queryKey: ['sms-unmatched', smsFilters.date_from, smsFilters.date_to],
    queryFn: () => {
      const params = new URLSearchParams();
      if (smsFilters.date_from) params.append('date_from', smsFilters.date_from);
      if (smsFilters.date_to) params.append('date_to', smsFilters.date_to);
      return api.get(`/sms-verification/unmatched?${params.toString()}`);
    },
    enabled: smsVerificationView === 'unmatched'
  });

  // 일괄 매칭 뮤테이션
  const batchMatchMutation = useMutation({
    mutationFn: (data) => api.post('/sms-verification/batch-match', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['sms-daily-details']);
      queryClient.invalidateQueries(['sms-unmatched']);
      queryClient.invalidateQueries(['sms-payments']);
      alert('일괄 매칭이 완료되었습니다.');
    },
    onError: (error) => {
      alert(`일괄 매칭 중 오류가 발생했습니다: ${error.message || error}`);
    }
  });

  // 일괄 입금 처리 뮤테이션
  const batchProcessMutation = useMutation({
    mutationFn: (data) => api.post('/sms-verification/batch-process', data),
    onSuccess: (response) => {
      const result = response || {};
      const processedCount = result.data?.processed_count || 0;
      const failedProcesses = result.data?.failed_processes || [];
      const message = result.message || `일괄 입금 처리가 완료되었습니다. (처리: ${processedCount}건, 실패: ${failedProcesses.length}건)`;
      
      // 모든 관련 쿼리 무효화 및 재조회
      queryClient.invalidateQueries(['sms-daily-details']);
      queryClient.invalidateQueries(['sms-daily-summary']);
      queryClient.invalidateQueries(['sms-payments']);
      queryClient.invalidateQueries(['payment-dashboard']);
      queryClient.invalidateQueries(['billing-payment-summary']);
      
      // 청구건별종합 쿼리 즉시 재조회 (모든 필터 조건에 대해)
      queryClient.refetchQueries(['billing-payment-summary'], { active: true });
      
      // 실패한 항목이 있으면 상세 정보 표시
      if (failedProcesses.length > 0) {
        const failedReasons = failedProcesses.map(f => `SMS ID ${f.sms_id}: ${f.reason}`).join('\n');
        alert(`${message}\n\n실패한 항목:\n${failedReasons}`);
      } else {
        alert(message);
      }
      
      setSelectedSmsIds([]);
      
      // 데이터 즉시 재조회
      if (smsVerificationView === 'daily' && selectedDate) {
        refetchDailyDetails();
      }
      if (smsVerificationView === 'summary') {
        queryClient.refetchQueries(['sms-daily-summary']);
      }
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || error;
      alert(`일괄 입금 처리 중 오류가 발생했습니다: ${errorMessage}`);
      console.error('일괄 입금 처리 오류:', error);
    }
  });

  // SMS 입금내역
  const { data: smsPaymentsData, isLoading: smsPaymentsLoading } = useQuery({
    queryKey: ['sms-payments', smsFilters],
    queryFn: () => {
      const params = new URLSearchParams();
      if (smsFilters.bank_name) {
        params.append('bank_name', smsFilters.bank_name);
      }
      if (smsFilters.status && smsFilters.status !== 'all') {
        params.append('status', smsFilters.status);
      }
      if (smsFilters.date_from) {
        params.append('date_from', smsFilters.date_from);
      }
      if (smsFilters.date_to) {
        params.append('date_to', smsFilters.date_to);
      }
      if (smsFilters.property_owner_id) {
        params.append('property_owner_id', smsFilters.property_owner_id);
      }
      
      const queryString = params.toString();
      const url = `/payment/sms-payments${queryString ? `?${queryString}` : ''}`;
      console.log('🔍 SMS입금 API 호출:', url);
      return api.get(url);
    }
  });

  const { data: bankSmsPaymentsData, isLoading: bankSmsPaymentsLoading } = useQuery({
    queryKey: ['bank-sms-payments'],
    queryFn: () => api.get('/payment/bank-sms-payments?per_page=50')
  });

  const applyBankSmsPaymentMutation = useMutation({
    mutationFn: ({ matchId, billingStatementId }) =>
      api.post(`/payment/bank-sms-payments/${matchId}/apply`, {
        billing_statement_id: billingStatementId
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(['bank-sms-payments']);
      queryClient.invalidateQueries(['billing-payment-summary']);
      queryClient.invalidateQueries(['payment-dashboard']);
      queryClient.invalidateQueries(['sms-payments']);
      alert('Bank SMS 입금이 기존 수납원장에 적용되었습니다.');
    },
    onError: (error) => {
      const message = error.response?.data?.message || error.response?.data?.error || error.message || error;
      alert(`Bank SMS 입금 적용 실패: ${message}`);
    }
  });

  // 은행별 SMS입금 통계
  const { data: bankSummaryData } = useQuery({
    queryKey: ['sms-payments-bank-summary'],
    queryFn: () => api.get('/payment/sms-payments/bank-summary')
  });

  // 은행별 SMS 포맷 정보
  const { data: smsFormatsData } = useQuery({
    queryKey: ['sms-formats'],
    queryFn: () => api.get('/payment/sms-formats')
  });

  // 연체 관리
  const { data: overdueData, isLoading: overdueLoading } = useQuery({
    queryKey: ['overdue-management'],
    queryFn: () => api.get('/payment/overdue')
  });

  // 임차인 목록
  const { data: tenantsData } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => api.get('/tenants')
  });

  // 건물 목록
  const { data: buildings } = useQuery({
    queryKey: ['buildings', searchFilters.property_owner_id],
    queryFn: async () => {
      let url = '/buildings';
      if (searchFilters.property_owner_id) {
        url += `?property_owner_id=${searchFilters.property_owner_id}`;
      }
      const response = await api.get(url);
      return Array.isArray(response) ? response : response?.data || [];
    },
    onSuccess: (data) => {
      console.log('🔍 Buildings Data:', data);
    },
    onError: (error) => {
      console.error('❌ Buildings Error:', error);
    }
  });

  // 사용자 정보 가져오기 (RMC 사용자 확인용)
  const { data: userInfo } = useQuery({
    queryKey: ['user-info'],
    queryFn: () => api.get('/auth/me'),
    onSuccess: (data) => {
      console.log('🔍 User Info:', data);
    }
  });

  // 임대사업자 목록 (RMC 사용자용)
  const { data: propertyOwners } = useQuery({
    queryKey: ['property-owners', userInfo?.role],
    queryFn: () => api.get('/property-owners'),
    enabled: userInfo?.role === 'rental_management_company' || 
             userInfo?.role === 'rmc_admin' || 
             userInfo?.role === 'rmc_staff' ||
             (userInfo?.user_type === 'rmc_user' && userInfo?.role === 'admin'),
    staleTime: 5 * 60 * 1000, // 5분
    cacheTime: 10 * 60 * 1000, // 10분
    onSuccess: (data) => {
      console.log('🔍 Property Owners Data:', data);
    },
    onError: (error) => {
      console.error('❌ Property Owners Error:', error);
    }
  });

  // 청구서 목록 (임시 비활성화)
  // const { data: billingStatementsData } = useQuery({
  //   queryKey: ['billing-statements'],
  //   queryFn: () => api.get('/billing/statements')
  // });


  // 테스트 API 제거

  // 청구건별 입금내역 종합 조회
  const { data: billingPaymentSummaryData, isLoading: billingPaymentSummaryLoading, error: billingPaymentSummaryError } = useQuery({
    queryKey: ['billing-payment-summary', searchFilters],
    queryFn: () => {
      const params = new URLSearchParams();
      if (searchFilters.status && searchFilters.status !== 'all') {
        params.append('status', searchFilters.status);
      }
      if (searchFilters.billing_month) {
        params.append('billing_month', searchFilters.billing_month);
      }
      if (searchFilters.building_id) {
        params.append('building_id', searchFilters.building_id);
      }
      if (searchFilters.tenant_name) {
        params.append('tenant_name', searchFilters.tenant_name);
      }
      if (searchFilters.property_owner_id) {
        params.append('property_owner_id', searchFilters.property_owner_id);
      }
      
      const queryString = params.toString();
      const url = `/payment/billing-payment-summary${queryString ? `?${queryString}` : ''}`;
      console.log('🔍 청구건별종합 API 호출:', url);
      console.log('🔍 필터 파라미터:', searchFilters);
      return api.get(url);
    },
    onSuccess: (data) => {
      console.log('🔍 청구건별종합 API 응답:', data);
      console.log('🔍 응답 데이터:', data?.data);
      console.log('🔍 응답 요약:', data?.summary);
    },
    onError: (error) => {
      console.error('❌ 청구건별종합 API 오류:', error);
    }
  });




  // 수금 활동 기록 뮤테이션
  const addCollectionActivityMutation = useMutation({
    mutationFn: ({ overdueId, activityData }) => 
      api.post(`/payment/overdue/${overdueId}/activities`, activityData),
    onSuccess: () => {
      queryClient.invalidateQueries(['overdue-management']);
      setShowActivityModal(false);
    }
  });


  // 수납관리 액션 뮤테이션
  const paymentActionMutation = useMutation({
    mutationFn: ({ statementId, action, data }) => 
      api.post(`/payment/billing-statements/${statementId}/payment-actions`, { action, ...data }),
    onSuccess: () => {
      queryClient.invalidateQueries(['billing-payment-summary']);
      queryClient.invalidateQueries(['billing-statements-for-payment']);
      queryClient.invalidateQueries(['payments']);
      queryClient.invalidateQueries(['payment-dashboard']);
      setShowPaymentActionModal(false);
      setSelectedPaymentAction(null);
    }
  });


  // SMS 입금 등록 뮤테이션
  const registerSmsPaymentMutation = useMutation({
    mutationFn: (smsData) => {
      // 수동 등록 시 필요한 데이터 변환
      const payload = {
        tenant_phone: smsData.tenant_phone,
        amount: parseInt(smsData.amount),
        bank_name: smsData.bank_name || '',
        account_number: smsData.account_number || '',
        transaction_time: smsData.transaction_time,
        sender_name: smsData.sender_name || smsData.tenant_name || '',
        notes: smsData.notes || ''
      };
      return api.post('/payment/sms-payments', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['sms-payments']);
      queryClient.invalidateQueries(['sms-daily-summary']);
      queryClient.invalidateQueries(['payments']);
      queryClient.invalidateQueries(['payment-dashboard']);
      setShowSmsPaymentModal(false);
      alert('SMS 입금 데이터가 등록되었습니다.');
    },
    onError: (error) => {
      alert(`SMS 등록 중 오류가 발생했습니다: ${error.message || error}`);
    }
  });

  // SMS 시뮬레이터 뮤테이션
  const sendSmsMutation = useMutation({
    mutationFn: (data) => {
      console.log('SMS 전송 API 호출 시작...', data);
      return api.post('/payment/sms/receive', data);
    },
    onSuccess: (data) => {
      console.log('SMS 전송 성공:', data);
      queryClient.invalidateQueries(['sms-payments']);
      queryClient.invalidateQueries(['sms-payments-bank-summary']);
      alert('SMS 전송이 완료되었습니다!');
    },
    onError: (error) => {
      console.error('SMS 전송 오류:', error);
      alert(`SMS 전송 중 오류가 발생했습니다: ${error.message || error}`);
    }
  });

  // SMS 입금 데이터 상태 변경 뮤테이션
  const updateSmsStatusMutation = useMutation({
    mutationFn: ({ smsId, status, notes }) => {
      console.log('SMS 상태 변경 API 호출:', { smsId, status, notes });
      return api.put(`/payment/sms-payments/${smsId}/status`, { status, notes });
    },
    onSuccess: (data) => {
      console.log('SMS 상태 변경 성공:', data);
      queryClient.invalidateQueries(['sms-payments']);
      queryClient.invalidateQueries(['sms-payments-bank-summary']);
      alert(data.message || '상태가 변경되었습니다.');
    },
    onError: (error) => {
      console.error('SMS 상태 변경 오류:', error);
      alert(`상태 변경 중 오류가 발생했습니다: ${error.message || error}`);
    }
  });

  // SMS 입금 데이터 수동 매칭 뮤테이션
  const manualMatchMutation = useMutation({
    mutationFn: ({ smsId, billingStatementId, notes }) => {
      console.log('SMS 수동 매칭 API 호출:', { smsId, billingStatementId, notes });
      return api.post(`/payment/sms-payments/${smsId}/match`, { billing_statement_id: billingStatementId, notes });
    },
    onSuccess: (data) => {
      console.log('SMS 수동 매칭 성공:', data);
      queryClient.invalidateQueries(['sms-payments']);
      queryClient.invalidateQueries(['sms-payments-bank-summary']);
      setShowManualMatchModal({ show: false, smsRecord: null });
      alert(data.message || '매칭이 완료되었습니다.');
    },
    onError: (error) => {
      console.error('SMS 수동 매칭 오류:', error);
      alert(`매칭 중 오류가 발생했습니다: ${error.message || error}`);
    }
  });

  // SMS 입금 데이터 청구서 검색 핸들러
  const handleSearchBilling = (smsId) => {
    console.log('🔍 청구서 검색 시작:', smsId);
    
    // fetch로 직접 테스트
    const token = localStorage.getItem('token');
    fetch(`/api/payment/sms-payments/${smsId}/search-billing`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    .then(response => {
      console.log('🔍 fetch 응답 상태:', response.status);
      console.log('🔍 fetch 응답 헤더:', response.headers);
      return response.json();
    })
    .then(data => {
      console.log('🔍 fetch 응답 데이터:', data);
      
      if (data.success) {
        console.log('✅ 청구서 검색 성공:', data);
        // 검색 결과를 모달로 표시
        setShowBillingSearchModal({ 
          show: true, 
          smsId, 
          matches: data.matches || [],
          smsRecord: data.sms_record
        });
        // 모달 표시 후에 데이터 새로고침
        queryClient.invalidateQueries(['sms-payments']);
      } else {
        console.log('❌ 청구서 검색 실패:', data);
        alert(data.message || '청구서 검색에 실패했습니다.');
        // 실패 시에만 데이터 새로고침
        queryClient.invalidateQueries(['sms-payments']);
      }
    })
    .catch(error => {
      console.error('❌ fetch 오류:', error);
      alert(`청구서 검색 중 오류가 발생했습니다: ${error.message || error}`);
    });
  };

  // SMS 입금 데이터 청구서 선택 핸들러
  const handleSelectBilling = (smsId, billingStatementId) => {
    const notes = window.prompt('매칭 메모를 입력하세요 (선택사항):');
    if (notes === null) return; // 취소된 경우
    
    manualMatchMutation.mutate({
      smsId,
      billingStatementId,
      notes: notes || ''
    });
  };

  // SMS 입금 데이터 입금처리 핸들러
  const handleProcessPayment = (smsId, billingStatementId) => {
    console.log('🔍 입금처리 시작:', { smsId, billingStatementId });
    
    // billingStatementId가 null이거나 유효하지 않은 경우 처리
    if (!billingStatementId || billingStatementId === 'null' || billingStatementId === null) {
      console.error('❌ 유효하지 않은 billingStatementId:', billingStatementId);
      alert('청구서 ID가 유효하지 않습니다. 먼저 청구서를 매칭해주세요.');
      return;
    }
    
    // 청구서 정보를 먼저 조회하여 확인 모달 표시
    api.get(`/payment/billing-statements/${billingStatementId}`)
      .then(response => {
        console.log('✅ 청구서 조회 성공:', response.data);
        const billingStatement = response.data.data;
        setShowBillingConfirmationModal({
          show: true,
          smsId: smsId,
          billingStatement: billingStatement,
          action: 'process'
        });
      })
      .catch(error => {
        console.error('❌ 청구서 조회 오류:', error);
        alert('청구서 정보를 조회할 수 없습니다.');
      });
  };

  // SMS 입금 데이터 매칭취소 핸들러
  const handleCancelMatch = (smsId, billingStatementId) => {
    console.log('🔍 매칭취소 시작:', { smsId, billingStatementId });
    
    // billingStatementId가 null이거나 유효하지 않은 경우 처리
    if (!billingStatementId || billingStatementId === 'null' || billingStatementId === null) {
      console.error('❌ 유효하지 않은 billingStatementId:', billingStatementId);
      alert('청구서 ID가 유효하지 않습니다. 먼저 청구서를 매칭해주세요.');
      return;
    }
    
    // 청구서 정보를 먼저 조회하여 확인 모달 표시
    api.get(`/payment/billing-statements/${billingStatementId}`)
      .then(response => {
        console.log('✅ 청구서 조회 성공:', response.data);
        const billingStatement = response.data.data;
        setShowBillingConfirmationModal({
          show: true,
          smsId: smsId,
          billingStatement: billingStatement,
          action: 'cancel'
        });
      })
      .catch(error => {
        console.error('❌ 청구서 조회 오류:', error);
        alert('청구서 정보를 조회할 수 없습니다.');
      });
  };

  // SMS 입금 데이터 문제성 자료 처리 핸들러
  const handleMarkAsProblematic = (smsId) => {
    const notes = window.prompt('문제 사유를 입력하세요:');
    if (notes === null || !notes.trim()) return; // 취소된 경우 또는 빈 값
    
    updateSmsStatusMutation.mutate({
      smsId,
      status: 'unmatched',
      notes: notes.trim()
    });
  };


  // 수납관리 액션 핸들러
  const handlePaymentAction = (statementId, action, data) => {
    setSelectedPaymentAction({ statementId, action, data });
    setShowPaymentActionModal(true);
  };

  const tabs = [
    { id: 'dashboard', name: '대시보드', icon: CurrencyDollarIcon },
    { id: 'billing-payment-summary', name: '청구건별 종합', icon: DocumentTextIcon },
    { id: 'bank-sms-payments', name: '자동수집 입금', icon: CheckCircleIcon },
    { id: 'sms-payments', name: 'SMS 입금내역', icon: PhoneIcon },
    { id: 'overdue', name: '연체 관리', icon: ExclamationTriangleIcon }
  ];

  const renderBankSmsPayments = () => {
    const items = bankSmsPaymentsData?.items || [];

    return (
      <div className="space-y-6">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">자동수집 입금</h2>
            <p className="mt-1 text-sm text-gray-500">은행 SMS Collector에서 수집한 입금 후보입니다.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">은행</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">입금일시</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">금액</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">입금자</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">추천 청구건</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">상태</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">처리</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {bankSmsPaymentsLoading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-sm text-gray-500">불러오는 중...</td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-sm text-gray-500">자동수집 입금 후보가 없습니다.</td>
                  </tr>
                ) : (
                  items.map((item) => {
                    const statement = item.billing_statement;
                    const canApply = !item.applied && item.matched_billing_statement_id && item.parse_status === 'PARSED';
                    return (
                      <tr key={item.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.bank_code || '-'} <span className="text-xs text-gray-500">({item.parser_confidence || '-'})</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.transaction_at ? new Date(item.transaction_at).toLocaleString() : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                          {(item.amount || 0).toLocaleString()}원
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>{item.sender_name || '-'}</div>
                          <div className="text-xs text-gray-500">{item.account_masked || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {statement ? (
                            <div>
                              <div>{statement.building_name || ''} {statement.unit_number || ''}호</div>
                              <div className="text-xs text-gray-500">
                                {statement.tenant_name || '-'} · {statement.billing_month || '-'} · {(statement.total_amount || 0).toLocaleString()}원
                              </div>
                            </div>
                          ) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            item.applied ? 'bg-green-100 text-green-800' :
                            item.match_status === 'AUTO_MATCHED' ? 'bg-blue-100 text-blue-800' :
                            item.match_status === 'REVIEW_REQUIRED' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {item.applied ? '적용완료' : item.match_status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            type="button"
                            disabled={!canApply || applyBankSmsPaymentMutation.isPending}
                            onClick={() => {
                              if (window.confirm('이 Bank SMS 입금을 기존 수납원장에 적용할까요?')) {
                                applyBankSmsPaymentMutation.mutate({
                                  matchId: item.id,
                                  billingStatementId: item.matched_billing_statement_id
                                });
                              }
                            }}
                            className={`px-3 py-1 rounded text-sm ${
                              canApply
                                ? 'bg-blue-600 text-white hover:bg-blue-700'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            적용
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderDashboard = () => {
    if (dashboardLoading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    const data = dashboardData || {};

    return (
      <div className="space-y-6">
        {/* 통계 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">이번 달 입금</p>
                <p className="text-2xl font-bold text-gray-900">
                  {data.monthly_payments?.total_amount?.toLocaleString() || 0}원
                </p>
                <p className="text-sm text-gray-500">
                  {data.monthly_payments?.payment_count || 0}건
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">연체 금액</p>
                <p className="text-2xl font-bold text-gray-900">
                  {data.overdue_stats?.total_overdue?.toLocaleString() || 0}원
                </p>
                <p className="text-sm text-gray-500">
                  {data.overdue_stats?.overdue_count || 0}건
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <PhoneIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">SMS 입금내역</p>
                <p className="text-2xl font-bold text-gray-900">
                  {data.sms_stats?.total_records || 0}건
                </p>
                <p className="text-sm text-gray-500">
                  미처리 {data.sms_stats?.pending_records || 0}건
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <ClockIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">긴급 연체</p>
                <p className="text-2xl font-bold text-gray-900">
                  {data.urgent_overdue?.length || 0}건
                </p>
                <p className="text-sm text-gray-500">30일 이상</p>
              </div>
            </div>
          </div>
        </div>

        {/* 최근 입금 내역 */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">최근 입금 내역</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    임차인
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    금액
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    입금일
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    방법
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.recent_payments?.map((payment) => (
                  <tr key={payment.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {payment.tenant?.name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {payment.amount?.toLocaleString()}원
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(payment.payment_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {payment.payment_method === 'sms_auto' ? 'SMS 자동' : '수동'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 긴급 연체 건 */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">긴급 연체 건</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    임차인
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    연체금액
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    연체일수
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    담당자
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.urgent_overdue?.map((overdue) => (
                  <tr key={overdue.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {overdue.tenant?.name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                      {overdue.overdue_amount?.toLocaleString()}원
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {overdue.overdue_days}일
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {overdue.assignee?.name || '미배정'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };



  const renderBillingPaymentSummary = () => {
    if (billingPaymentSummaryLoading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (billingPaymentSummaryError) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="text-red-600">데이터를 불러오는 중 오류가 발생했습니다: {billingPaymentSummaryError.message}</div>
        </div>
      );
    }

    console.log('🔍 renderBillingPaymentSummary - billingPaymentSummaryData:', billingPaymentSummaryData);
    const summaryData = billingPaymentSummaryData?.data || [];
    const summary = billingPaymentSummaryData?.summary || {};
    console.log('🔍 renderBillingPaymentSummary - summaryData:', summaryData);
    console.log('🔍 renderBillingPaymentSummary - summary:', summary);

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900">청구건별 입금내역 종합</h2>
        </div>

        {/* 검색 및 필터 */}
        <div className="bg-white p-4 rounded-lg shadow">
          <div className={`grid grid-cols-1 gap-3 ${(userInfo?.role === 'rental_management_company' || 
                                                      userInfo?.role === 'rmc_admin' || 
                                                      userInfo?.role === 'rmc_staff' ||
                                                      (userInfo?.user_type === 'rmc_user' && userInfo?.role === 'admin')) ? 'md:grid-cols-6' : 'md:grid-cols-5'}`}>
            {/* 상태 필터 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">입금상태</label>
              <select
                value={searchFilters.status}
                onChange={(e) => setSearchFilters({...searchFilters, status: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">전체</option>
                <option value="pending">미납</option>
                <option value="paid">납부완료</option>
                <option value="partial">부분납부</option>
                <option value="overdue">연체</option>
              </select>
            </div>

            {/* 청구월 필터 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">청구월</label>
              <input
                type="month"
                value={searchFilters.billing_month}
                onChange={(e) => setSearchFilters({...searchFilters, billing_month: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* 임대사업자 필터 (RMC 사용자용) */}
            {(userInfo?.role === 'rental_management_company' || 
              userInfo?.role === 'rmc_admin' || 
              userInfo?.role === 'rmc_staff' ||
              (userInfo?.user_type === 'rmc_user' && userInfo?.role === 'admin')) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">임대사업자</label>
                <select
                  value={searchFilters.property_owner_id}
                onChange={(e) => {
                  const newFilters = {...searchFilters, property_owner_id: e.target.value, building_id: ''};
                  console.log('🔍 임대사업자 선택:', e.target.value);
                  console.log('🔍 새로운 필터:', newFilters);
                  setSearchFilters(newFilters);
                }}
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">전체 임대사업자</option>
                  {propertyOwners?.map((owner) => (
                    <option key={owner.id} value={owner.id}>
                      {owner.company_name || owner.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* 건물 필터 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">건물</label>
              <select
                value={searchFilters.building_id}
                onChange={(e) => setSearchFilters({...searchFilters, building_id: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={(userInfo?.role === 'rental_management_company' || 
                          userInfo?.role === 'rmc_admin' || 
                          userInfo?.role === 'rmc_staff' ||
                          (userInfo?.user_type === 'rmc_user' && userInfo?.role === 'admin')) && 
                         !searchFilters.property_owner_id}
              >
                <option value="">전체 건물</option>
                {buildings?.map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </select>
            </div>

            {/* 임차인명 검색 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">임차인명</label>
              <input
                type="text"
                placeholder="임차인명 검색"
                value={searchFilters.tenant_name}
                onChange={(e) => setSearchFilters({...searchFilters, tenant_name: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* 필터 초기화 버튼 */}
            <div className="flex items-end">
              <button
                onClick={() => setSearchFilters({
                  status: 'all',
                  billing_month: '',
                  building_id: '',
                  tenant_name: '',
                  property_owner_id: ''
                })}
                className="w-full px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:ring-2 focus:ring-blue-500"
              >
                필터 초기화
              </button>
            </div>
          </div>
        </div>

        {/* 요약 통계 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">총 청구건</p>
                <p className="text-2xl font-semibold text-gray-900">{summary.total_statements || 0}건</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-8 w-8 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">미납</p>
                <p className="text-2xl font-semibold text-gray-900">{summary.pending_count || 0}건</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">납부완료</p>
                <p className="text-2xl font-semibold text-gray-900">{summary.paid_count || 0}건</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-8 w-8 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">부분납부</p>
                <p className="text-2xl font-semibold text-gray-900">{summary.partial_count || 0}건</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">연체</p>
                <p className="text-2xl font-semibold text-gray-900">{summary.overdue_count || 0}건</p>
                <p className="text-xs text-gray-400 mt-1">(미납+부분납부 중 납기일 경과)</p>
              </div>
            </div>
          </div>
        </div>

        {/* 청구건별 상세 테이블 */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">청구건별 입금상태</h3>
          </div>
          
          {summaryData.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">청구내역이 없습니다</h3>
              <p className="mt-1 text-sm text-gray-500">
                검색 조건에 맞는 청구내역이 없습니다. 필터를 조정해보세요.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      상태
                    </th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      청구서번호
                    </th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      건물명
                    </th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      호수
                    </th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      세입자명
                    </th>
                    <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      청구액
                    </th>
                    <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      납입액
                    </th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      결제수단
                    </th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      납입일
                    </th>
                    <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      수납관리
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {summaryData.map((item) => (
                    <tr key={item.statement_id} className="hover:bg-gray-50">
                      {/* 상태 */}
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                          item.payment_status === 'paid' 
                            ? 'bg-green-100 text-green-800' 
                            : item.payment_status === 'partial'
                            ? 'bg-yellow-100 text-yellow-800'
                            : item.is_overdue
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {item.payment_status === 'paid' ? '완납' : 
                           item.payment_status === 'partial' ? '부분납' :
                           item.is_overdue ? '연체' : '미납'}
                        </span>
                      </td>
                      
                      {/* 청구서번호 */}
                      <td className="px-2 py-1.5 whitespace-nowrap text-xs font-medium text-gray-900">
                        {item.statement_number}
                      </td>
                      
                      {/* 건물명 */}
                      <td className="px-2 py-1.5 whitespace-nowrap text-xs text-gray-900">
                        {item.building_name}
                      </td>
                      
                      {/* 호수 */}
                      <td className="px-2 py-1.5 whitespace-nowrap text-xs text-gray-900">
                        {item.unit_number}호
                      </td>
                      
                      {/* 세입자명 */}
                      <td className="px-2 py-1.5 whitespace-nowrap text-xs text-gray-900">
                        {item.tenant_name}
                      </td>
                      
                      {/* 청구액 */}
                      <td className="px-2 py-1.5 whitespace-nowrap text-xs text-gray-900 text-right">
                        {item.total_amount?.toLocaleString()}원
                      </td>
                      
                      {/* 납입액 */}
                      <td className="px-2 py-1.5 whitespace-nowrap text-xs text-gray-900 text-right">
                        {item.total_paid?.toLocaleString()}원
                      </td>
                      
                      {/* 결제수단 */}
                      <td className="px-2 py-1.5 whitespace-nowrap text-xs text-gray-500">
                        {item.payment_method === '미납' ? (
                          <span className="text-gray-400">-</span>
                        ) : (
                          <span className="capitalize">
                            {item.payment_method === 'bank_transfer' ? '계좌이체' :
                             item.payment_method === 'manual' ? '수동' :
                             item.payment_method === 'cash' ? '현금' :
                             item.payment_method === 'check' ? '수표' : item.payment_method}
                          </span>
                        )}
                      </td>
                      
                      {/* 납입일 */}
                      <td className="px-2 py-1.5 whitespace-nowrap text-xs text-gray-500">
                        {item.payment_date ? new Date(item.payment_date).toLocaleDateString() : '-'}
                      </td>
                      
                      
                      {/* 수납관리 */}
                      <td className="px-2 py-1.5 whitespace-nowrap text-center">
                        <PaymentManagementDropdown 
                          item={item}
                          onAction={(action, data) => handlePaymentAction(item.statement_id, action, data)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };


  const renderSmsPayments = () => {
    const payments = smsPaymentsData?.data || [];
    const bankSummary = bankSummaryData || [];
    const dailySummary = dailySummaryData?.data?.daily_summary || [];
    const dailyDetails = dailyDetailsData?.data?.sms_records || [];
    const unmatchedRecords = unmatchedSmsData?.data?.unmatched_records || [];

    return (
      <div className="space-y-6">
        {/* 뷰 전환 탭 */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setSmsVerificationView('summary')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                smsVerificationView === 'summary'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              일별 요약
            </button>
            <button
              onClick={() => {
                // 일별 상세 탭 클릭 시 selectedDate가 없으면 기본값 설정
                if (!selectedDate) {
                  // 일별 요약 데이터가 있으면 가장 최근 날짜 사용, 없으면 오늘 날짜 사용
                  if (dailySummary && dailySummary.length > 0) {
                    setSelectedDate(dailySummary[0].date);
                  } else {
                    const today = new Date().toISOString().split('T')[0];
                    setSelectedDate(today);
                  }
                }
                setSmsVerificationView('daily');
              }}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                smsVerificationView === 'daily'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              일별 상세
            </button>
            <button
              onClick={() => setSmsVerificationView('unmatched')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                smsVerificationView === 'unmatched'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              미매칭 데이터
            </button>
            <button
              onClick={() => setSmsVerificationView('list')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                smsVerificationView === 'list'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              전체 목록
            </button>
          </nav>
        </div>

        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">SMS 입금내역</h2>
        </div>

        {/* 은행별 통계 카드 */}
        {bankSummary.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {bankSummary.map((bank) => (
              <div key={bank.bank_name} className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-bold">
                          {bank.bank_name?.charAt(0) || '?'}
                        </span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          {bank.bank_name || '미분류'}
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {bank.total_amount?.toLocaleString()}원
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-5 py-3">
                  <div className="text-sm">
                    <span className="text-gray-500">총 {bank.total_count}건</span>
                    <span className="ml-2 text-green-600">완료 {bank.processed_count}건</span>
                    <span className="ml-2 text-yellow-600">대기 {bank.pending_count}건</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 필터링 옵션 - 일별 요약/미매칭/전체 목록에서만 표시 */}
        {smsVerificationView !== 'daily' && (
        <div className="bg-white p-4 rounded-lg shadow">
          <div className={`grid grid-cols-1 gap-4 ${(userInfo?.role === 'rental_management_company' || 
                                                      userInfo?.role === 'rmc_admin' || 
                                                      userInfo?.role === 'rmc_staff' ||
                                                      (userInfo?.user_type === 'rmc_user' && userInfo?.role === 'admin')) ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
            {/* 임대사업자 필터 (RMC 사용자용) */}
            {(userInfo?.role === 'rental_management_company' || 
              userInfo?.role === 'rmc_admin' || 
              userInfo?.role === 'rmc_staff' ||
              (userInfo?.user_type === 'rmc_user' && userInfo?.role === 'admin')) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">임대사업자</label>
                <select
                  value={smsFilters.property_owner_id}
                  onChange={(e) => setSmsFilters({...smsFilters, property_owner_id: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">전체 임대사업자</option>
                  {propertyOwners?.map((owner) => (
                    <option key={owner.id} value={owner.id}>
                      {owner.company_name || owner.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">은행명</label>
              <select
                value={smsFilters.bank_name}
                onChange={(e) => setSmsFilters({...smsFilters, bank_name: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">전체 은행</option>
                {bankSummary.map((bank) => (
                  <option key={bank.bank_name} value={bank.bank_name}>
                    {bank.bank_name || '미분류'}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">처리상태</label>
              <select
                value={smsFilters.status}
                onChange={(e) => setSmsFilters({...smsFilters, status: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">전체</option>
                <option value="pending">대기</option>
                <option value="matched">매칭됨</option>
                <option value="unmatched">미매칭</option>
                <option value="processed">처리완료</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">시작일</label>
              <input
                type="date"
                value={smsFilters.date_from}
                onChange={(e) => setSmsFilters({...smsFilters, date_from: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">종료일</label>
              <input
                type="date"
                value={smsFilters.date_to}
                onChange={(e) => setSmsFilters({...smsFilters, date_to: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => setSmsFilters({
                bank_name: '',
                status: 'all',
                date_from: '',
                date_to: '',
                property_owner_id: ''
              })}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              필터 초기화
            </button>
          </div>
        </div>
        )}

        {/* 일별 요약 뷰 */}
        {smsVerificationView === 'summary' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg font-medium text-gray-900">일별 SMS 데이터 요약</h3>
              <p className="mt-1 text-sm text-gray-500">날짜별로 그룹화된 SMS 입금 데이터 통계</p>
            </div>
            {dailySummary.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-sm text-gray-500">조회 기간 내 SMS 데이터가 없습니다.</p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">날짜</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">총 건수</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">총 금액</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">대기</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">매칭됨</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">미매칭</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">처리완료</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">액션</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {dailySummary.map((day) => (
                    <tr key={day.date} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {new Date(day.date).toLocaleDateString('ko-KR')}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                        {day.total_count}건
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                        {day.total_amount.toLocaleString()}원
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-right">
                        {day.pending_count}건
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-blue-600 text-right">
                        {day.matched_count}건
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-red-600 text-right">
                        {day.unmatched_count}건
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-green-600 text-right">
                        {day.processed_count}건
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <button
                          onClick={() => {
                            setSelectedDate(day.date);
                            setSmsVerificationView('daily');
                          }}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          상세보기
                        </button>
                      </td>
                    </tr>
                  ))}
                  {/* 합계 행 */}
                  {dailySummary.length > 0 && (
                    <tr className="bg-gray-100 font-semibold">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        합계
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                        {dailySummary.reduce((sum, day) => sum + (day.total_count || 0), 0)}건
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                        {dailySummary.reduce((sum, day) => sum + (day.total_amount || 0), 0).toLocaleString()}원
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-right">
                        {dailySummary.reduce((sum, day) => sum + (day.pending_count || 0), 0)}건
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-blue-600 text-right">
                        {dailySummary.reduce((sum, day) => sum + (day.matched_count || 0), 0)}건
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-red-600 text-right">
                        {dailySummary.reduce((sum, day) => sum + (day.unmatched_count || 0), 0)}건
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-green-600 text-right">
                        {dailySummary.reduce((sum, day) => sum + (day.processed_count || 0), 0)}건
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        {/* 빈 셀 */}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* 일별 상세 뷰 */}
        {smsVerificationView === 'daily' && (
          <div className="space-y-4">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {selectedDate ? new Date(selectedDate).toLocaleDateString('ko-KR') : '날짜 선택'}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">해당 일자의 SMS 데이터 상세 정보</p>
                </div>
                <div className="flex space-x-2">
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                  <button
                    onClick={() => setSmsVerificationView('summary')}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    목록으로
                  </button>
                </div>
              </div>
              
              {/* 일별 상세용 간소화된 필터 */}
              {selectedDate && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">은행명</label>
                    <select
                      value={smsFilters.bank_name || ''}
                      onChange={(e) => setSmsFilters({...smsFilters, bank_name: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">전체 은행</option>
                      {bankSummary.map((bank) => (
                        <option key={bank.bank_name} value={bank.bank_name}>
                          {bank.bank_name || '미분류'}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">처리상태</label>
                    <select
                      value={smsFilters.status || 'all'}
                      onChange={(e) => setSmsFilters({...smsFilters, status: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">전체</option>
                      <option value="pending">대기</option>
                      <option value="matched">매칭됨</option>
                      <option value="unmatched">미매칭</option>
                      <option value="processed">처리완료</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">임대사업자</label>
                    <select
                      value={smsFilters.property_owner_id || ''}
                      onChange={(e) => setSmsFilters({...smsFilters, property_owner_id: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">전체 임대사업자</option>
                      <option value="unassigned">미할당</option>
                      {propertyOwners.map((owner) => (
                        <option key={owner.id} value={owner.id}>
                          {owner.company_name || owner.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={() => setSmsFilters({
                        bank_name: '',
                        status: 'all',
                        property_owner_id: ''
                      })}
                      className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      필터 초기화
                    </button>
                  </div>
                </div>
              )}
            </div>

            {!selectedDate && (
              <div className="text-center py-12 bg-white rounded-lg shadow">
                <p className="text-sm text-gray-500 mb-2">날짜를 선택해주세요.</p>
                <p className="text-xs text-gray-400">위의 날짜 입력 필드에서 날짜를 선택하거나, 일별 요약에서 날짜를 클릭하세요.</p>
              </div>
            )}
            {selectedDate && dailyDetails.length === 0 && (
              <div className="text-center py-12 bg-white rounded-lg shadow">
                <p className="text-sm text-gray-500">해당 날짜의 SMS 데이터가 없습니다.</p>
              </div>
            )}
            {selectedDate && dailyDetails.length > 0 && (() => {
              // 클라이언트 측 필터링 적용
              let filteredDetails = dailyDetails;
              
              if (smsFilters.bank_name) {
                filteredDetails = filteredDetails.filter(s => s.bank_name === smsFilters.bank_name);
              }
              
              if (smsFilters.status && smsFilters.status !== 'all') {
                filteredDetails = filteredDetails.filter(s => s.status === smsFilters.status);
              }
              
              return (
              <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <span className="text-sm font-medium text-gray-700">
                      총 {filteredDetails.length}건 {filteredDetails.length !== dailyDetails.length && `(전체 ${dailyDetails.length}건)`}
                    </span>
                    <span className="text-sm text-blue-600">
                      매칭됨: {filteredDetails.filter(s => s.status === 'matched').length}건
                    </span>
                    <span className="text-sm text-green-600">
                      처리완료: {filteredDetails.filter(s => s.status === 'processed').length}건
                    </span>
                    <span className="text-sm text-red-600">
                      미매칭: {filteredDetails.filter(s => s.status === 'unmatched' || s.status === 'pending').length}건
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        const matchedIds = filteredDetails
                          .filter(s => s.status === 'matched')
                          .map(s => s.id);
                        if (matchedIds.length > 0) {
                          batchProcessMutation.mutate({ sms_ids: matchedIds });
                        } else {
                          alert('매칭된 SMS 데이터가 없습니다.');
                        }
                      }}
                      disabled={batchProcessMutation.isPending}
                      className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                    >
                      {batchProcessMutation.isPending ? '처리 중...' : '매칭된 항목 일괄 처리'}
                    </button>
                  </div>
                </div>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedSmsIds(filteredDetails.map(s => s.id));
                            } else {
                              setSelectedSmsIds([]);
                            }
                          }}
                          checked={selectedSmsIds.length === filteredDetails.length && filteredDetails.length > 0}
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">시간</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">전화번호</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">송금자</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">금액</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">은행</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">상태</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">매칭 정보</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">액션</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredDetails.map((sms) => (
                      <tr key={sms.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedSmsIds.includes(sms.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedSmsIds([...selectedSmsIds, sms.id]);
                              } else {
                                setSelectedSmsIds(selectedSmsIds.filter(id => id !== sms.id));
                              }
                            }}
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {new Date(sms.transaction_time).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {sms.tenant_phone}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {sms.sender_name || '미확인'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                          {sms.amount.toLocaleString()}원
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {sms.bank_name || '미분류'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            sms.status === 'processed' ? 'bg-green-100 text-green-800' :
                            sms.status === 'matched' ? 'bg-blue-100 text-blue-800' :
                            sms.status === 'unmatched' ? 'bg-red-100 text-red-800' :
                            sms.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {sms.status === 'processed' ? '처리완료' :
                             sms.status === 'matched' ? '매칭됨' :
                             sms.status === 'unmatched' ? '미매칭' :
                             sms.status === 'pending' ? '대기' : sms.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {sms.matched_statement ? (
                            <div>
                              <div className="text-xs">청구월: {sms.matched_statement.billing_month}</div>
                              <div className="text-xs">임차인: {sms.matched_statement.tenant_name || 'N/A'}</div>
                            </div>
                          ) : sms.possible_matches && sms.possible_matches.length > 0 ? (
                            <div className="text-xs text-blue-600">
                              매칭 가능: {sms.possible_matches.length}건
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">매칭 정보 없음</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          {sms.status === 'matched' && (
                            <button
                              onClick={() => batchProcessMutation.mutate({ sms_ids: [sms.id] })}
                              className="text-green-600 hover:text-green-800 text-sm font-medium"
                            >
                              입금처리
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              );
            })()}
          </div>
        )}

        {/* 미매칭 데이터 뷰 */}
        {smsVerificationView === 'unmatched' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">미매칭 SMS 데이터</h3>
                  <p className="mt-1 text-sm text-gray-500">매칭되지 않은 SMS 데이터 확인 및 처리</p>
                </div>
                <div className="text-sm text-gray-500">
                  총 {unmatchedRecords.length}건
                </div>
              </div>
            </div>
            {unmatchedRecords.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-sm text-gray-500">미매칭 SMS 데이터가 없습니다.</p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">거래시간</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">전화번호</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">송금자</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">금액</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">은행</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">임차인</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">매칭 가능 청구서</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">액션</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {unmatchedRecords.map((sms) => (
                    <tr key={sms.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {new Date(sms.transaction_time).toLocaleString('ko-KR')}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {sms.tenant_phone}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {sms.sender_name || '미확인'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                        {sms.amount.toLocaleString()}원
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {sms.bank_name || '미분류'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {sms.tenant ? (
                          <div>
                            <div>{sms.tenant.name}</div>
                            <div className="text-xs text-gray-500">{sms.tenant.phone}</div>
                          </div>
                        ) : (
                          <span className="text-gray-400">임차인 정보 없음</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {sms.possible_statements && sms.possible_statements.length > 0 ? (
                          <div className="space-y-1">
                            {sms.possible_statements.map((stmt, idx) => (
                              <div key={idx} className="text-xs border-b border-gray-200 pb-1">
                                <div>청구월: {stmt.billing_month}</div>
                                <div>금액: {stmt.total_amount.toLocaleString()}원</div>
                                <div>납기일: {stmt.due_date ? new Date(stmt.due_date).toLocaleDateString('ko-KR') : 'N/A'}</div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">매칭 가능한 청구서 없음</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <button
                          onClick={() => handleSearchBilling(sms.id)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          청구서 검색
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* 전체 목록 뷰 */}
        {smsVerificationView === 'list' && payments.length === 0 && (
          <div className="text-center py-12">
            <PhoneIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">등록된 SMS 입금내역이 없습니다</h3>
            <p className="mt-1 text-sm text-gray-500">입금알림 SMS를 수동으로 등록해보세요.</p>
          </div>
        )}
        {smsVerificationView === 'list' && payments.length > 0 && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                    수신시간
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                    은행
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    송금자
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-18">
                    금액
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    임대사업자
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    상태
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-36">
                    액션
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      <div className="text-xs">
                        {new Date(payment.transaction_time || payment.received_at).toLocaleDateString('ko-KR')}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(payment.transaction_time || payment.received_at).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {payment.bank_name?.replace('은행', '') || '미분류'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {payment.sender_name || payment.parsed_sender_name || '미확인'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-center font-medium">
                      {payment.amount?.toLocaleString()}원
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      <div className="text-xs">
                        {payment.property_owner_name || '미확인'}
                      </div>
                      {payment.property_owner_name && (
                        <div className="text-xs text-gray-500">
                          {payment.property_owner_phone || ''}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        payment.status === 'processed'
                          ? 'bg-green-100 text-green-800'
                          : payment.status === 'matched'
                          ? 'bg-blue-100 text-blue-800'
                          : payment.status === 'review'
                          ? 'bg-yellow-100 text-yellow-800'
                          : payment.status === 'unmatched'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {payment.status === 'processed' ? '처리완료' : 
                         payment.status === 'matched' ? '매칭완료' : 
                         payment.status === 'review' ? '검토중' :
                         payment.status === 'unmatched' ? '문제성자료' : '대기'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <div className="flex flex-col space-y-1">
                        {payment.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleSearchBilling(payment.id)}
                              className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200"
                            >
                              청구서 검색
                            </button>
                            <button
                              onClick={() => handleMarkAsProblematic(payment.id)}
                              className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200"
                            >
                              문제성 자료
                            </button>
                          </>
                        )}
                        {payment.status === 'review' && (
                          <>
                            <button
                              onClick={() => handleSearchBilling(payment.id)}
                              className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200"
                            >
                              청구서 선택
                            </button>
                            <button
                              onClick={() => handleSearchBilling(payment.id)}
                              className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-gray-200"
                            >
                              재검색
                            </button>
                          </>
                        )}
                        {payment.status === 'matched' && (
                          <>
                            <button
                              onClick={() => handleProcessPayment(payment.id, payment.matched_billing_statement_id)}
                              className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200"
                            >
                              입금처리
                            </button>
                            <button
                              onClick={() => handleCancelMatch(payment.id, payment.matched_billing_statement_id)}
                              className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-gray-200"
                            >
                              매칭취소
                            </button>
                          </>
                        )}
                        {payment.status === 'processed' && (
                          <span className="text-xs text-green-600 font-medium">완료</span>
                        )}
                        {payment.status === 'unmatched' && (
                          <span className="text-xs text-red-600 font-medium">문제성 자료</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };


  const renderOverdue = () => {
    if (overdueLoading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    const overdueList = overdueData?.overdue_list || [];

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900">연체 관리</h2>
        </div>

        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {overdueList.map((overdue) => (
              <li key={overdue.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {overdue.tenant?.name || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500">
                        연체 {overdue.overdue_days}일 - 독촉 {overdue.reminder_count}회
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-medium text-red-600">
                      {overdue.overdue_amount?.toLocaleString()}원
                    </div>
                    <div className="text-sm text-gray-500">
                      납부기한: {new Date(overdue.due_date).toLocaleDateString()}
                    </div>
                    <button
                      onClick={() => {
                        setSelectedOverdue(overdue);
                        setShowActivityModal(true);
                      }}
                      className="mt-2 inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
                    >
                      수금 활동 기록
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                  >
                    <Icon className="h-5 w-5 mr-2" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="mt-6">
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'billing-payment-summary' && renderBillingPaymentSummary()}
        {activeTab === 'bank-sms-payments' && renderBankSmsPayments()}
        {activeTab === 'sms-payments' && renderSmsPayments()}
        {activeTab === 'overdue' && renderOverdue()}
          </div>
        </div>
      </div>


      {/* 수금 활동 기록 모달 */}
      {showActivityModal && selectedOverdue && (
        <CollectionActivityModal
          overdue={selectedOverdue}
          onClose={() => setShowActivityModal(false)}
          onSubmit={(data) => addCollectionActivityMutation.mutate({
            overdueId: selectedOverdue.id,
            activityData: data
          })}
          loading={addCollectionActivityMutation.isPending}
        />
      )}

      {/* 수납관리 액션 모달 */}
      {showPaymentActionModal && selectedPaymentAction && (
        <PaymentActionModal
          action={selectedPaymentAction}
          onClose={() => {
            setShowPaymentActionModal(false);
            setSelectedPaymentAction(null);
          }}
          onSubmit={(data) => paymentActionMutation.mutate({
            statementId: selectedPaymentAction.statementId,
            action: selectedPaymentAction.action,
            data: data
          })}
          loading={paymentActionMutation.isPending}
        />
      )}


      {/* SMS 입금 등록 모달 */}
      {showBillingSearchModal.show && (
        <BillingSearchModal
          smsId={showBillingSearchModal.smsId}
          matches={showBillingSearchModal.matches}
          smsRecord={showBillingSearchModal.smsRecord}
          onClose={() => setShowBillingSearchModal({ show: false, smsId: null, matches: [], smsRecord: null })}
          onSelectBilling={(billingStatement) => {
            console.log('✅ 청구서 선택됨:', billingStatement);
            // 청구서 선택 후 확인 모달 표시 (입금처리용)
            setShowBillingConfirmationModal({
              show: true,
              smsId: showBillingSearchModal.smsId,
              billingStatement,
              action: 'process'
            });
            // 검색 모달 닫기
            setShowBillingSearchModal({ show: false, smsId: null, matches: [], smsRecord: null });
          }}
        />
      )}

      {showBillingConfirmationModal.show && (
        <BillingConfirmationModal
          smsId={showBillingConfirmationModal.smsId}
          billingStatement={showBillingConfirmationModal.billingStatement}
          action={showBillingConfirmationModal.action}
          onClose={() => setShowBillingConfirmationModal({ show: false, smsId: null, billingStatement: null, action: null })}
          onConfirm={(notes) => {
            if (showBillingConfirmationModal.action === 'process') {
              // 입금처리 실행
              console.log('🔍 입금처리 API 호출 시작:', {
                smsId: showBillingConfirmationModal.smsId,
                billingStatementId: showBillingConfirmationModal.billingStatement.id,
                notes: notes || ''
              });
              
              // fetch로 직접 테스트
              const token = localStorage.getItem('token');
              fetch(`/api/payment/sms-payments/${showBillingConfirmationModal.smsId}/process-payment`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  billing_statement_id: showBillingConfirmationModal.billingStatement.id,
                  notes: notes || ''
                })
              })
              .then(response => {
                console.log('🔍 fetch 응답 상태:', response.status);
                console.log('🔍 fetch 응답 헤더:', response.headers);
                return response.json();
              })
              .then(data => {
                console.log('✅ 입금처리 성공:', data);
                if (data.success) {
                  queryClient.invalidateQueries(['sms-payments']);
                  queryClient.invalidateQueries(['sms-payments-bank-summary']);
                  alert(data.message || '입금처리가 완료되었습니다.');
                  setShowBillingConfirmationModal({ show: false, smsId: null, billingStatement: null, action: null });
                } else {
                  alert(`입금처리 실패: ${data.message || '알 수 없는 오류'}`);
                }
              })
              .catch(error => {
                console.error('❌ fetch 오류:', error);
                alert(`입금처리 중 오류가 발생했습니다: ${error.message || error.toString()}`);
              });
            } else if (showBillingConfirmationModal.action === 'cancel') {
              // 매칭취소 실행
              updateSmsStatusMutation.mutate({
                smsId: showBillingConfirmationModal.smsId,
                status: 'pending',
                notes: notes || '매칭 취소'
              });
              setShowBillingConfirmationModal({ show: false, smsId: null, billingStatement: null, action: null });
            } else if (showBillingConfirmationModal.action === 'match') {
              // 청구서 매칭 실행
              api.post(`/payment/sms-payments/${showBillingConfirmationModal.smsId}/match`, {
                billing_statement_id: showBillingConfirmationModal.billingStatement.id,
                notes: notes || ''
              }).then(response => {
                console.log('매칭 성공:', response.data);
                queryClient.invalidateQueries(['sms-payments']);
                queryClient.invalidateQueries(['sms-payments-bank-summary']);
                alert(response.data.message || '청구서 매칭이 완료되었습니다.');
                setShowBillingConfirmationModal({ show: false, smsId: null, billingStatement: null, action: null });
              }).catch(error => {
                console.error('매칭 오류:', error);
                const errorMessage = error.response?.data?.message || error.message || error.toString();
                alert(`매칭 중 오류가 발생했습니다: ${errorMessage}`);
              });
            }
          }}
        />
      )}
    </div>
  );
};

// 모달 컴포넌트들
const PaymentModal = ({ onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    contract_id: '',
    tenant_id: '',
    billing_statement_id: '',
    payment_date: new Date().toISOString().split('T')[0],
    amount: '',
    payment_type: 'monthly_rent',
    payment_method: 'manual',
    reference_number: '',
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">수동 입금 처리</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">계약 ID</label>
              <input
                type="number"
                value={formData.contract_id}
                onChange={(e) => setFormData({...formData, contract_id: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">임차인 ID</label>
              <input
                type="number"
                value={formData.tenant_id}
                onChange={(e) => setFormData({...formData, tenant_id: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">청구서 ID</label>
              <input
                type="number"
                value={formData.billing_statement_id}
                onChange={(e) => setFormData({...formData, billing_statement_id: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">입금일</label>
              <input
                type="date"
                value={formData.payment_date}
                onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">금액</label>
              <input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">납부 유형</label>
              <select
                value={formData.payment_type}
                onChange={(e) => setFormData({...formData, payment_type: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="monthly_rent">월세</option>
                <option value="maintenance_fee">관리비</option>
                <option value="deposit">보증금</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">입금 방법</label>
              <select
                value={formData.payment_method}
                onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="manual">수동</option>
                <option value="bank_transfer">계좌이체</option>
                <option value="cash">현금</option>
                <option value="check">수표</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">참조번호</label>
              <input
                type="text"
                value={formData.reference_number}
                onChange={(e) => setFormData({...formData, reference_number: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">메모</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                rows="3"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? '처리 중...' : '입금 처리'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const SmsPaymentModal = ({ onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    tenant_phone: '',
    amount: '',
    bank_name: '',
    account_number: '',
    transaction_time: new Date().toISOString().slice(0, 16),
    sender_name: '',
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">SMS 입금내역 등록</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">입금자 전화번호</label>
              <input
                type="tel"
                value={formData.tenant_phone}
                onChange={(e) => setFormData({...formData, tenant_phone: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">입금금액</label>
              <input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">은행명</label>
              <input
                type="text"
                value={formData.bank_name}
                onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">계좌번호</label>
              <input
                type="text"
                value={formData.account_number}
                onChange={(e) => setFormData({...formData, account_number: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">거래시간</label>
              <input
                type="datetime-local"
                value={formData.transaction_time}
                onChange={(e) => setFormData({...formData, transaction_time: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">송금자명</label>
              <input
                type="text"
                value={formData.sender_name}
                onChange={(e) => setFormData({...formData, sender_name: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">메모</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                rows="3"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? '등록 중...' : '등록'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const SmsMatchModal = ({ smsRecord, tenants, billingStatements, onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    tenant_id: '',
    billing_statement_id: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">SMS 입금내역 매칭</h3>
          <div className="mb-4 p-3 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-600">
              <strong>입금내역:</strong> {smsRecord.sender_name} - {smsRecord.amount?.toLocaleString()}원
            </p>
            <p className="text-sm text-gray-600">
              <strong>전화번호:</strong> {smsRecord.tenant_phone}
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">임차인</label>
              <select
                value={formData.tenant_id}
                onChange={(e) => setFormData({...formData, tenant_id: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              >
                <option value="">임차인 선택</option>
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name} ({tenant.phone})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">청구서</label>
              <select
                value={formData.billing_statement_id}
                onChange={(e) => setFormData({...formData, billing_statement_id: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              >
                <option value="">청구서 선택</option>
                {billingStatements
                  .filter(stmt => stmt.status === 'pending')
                  .map((statement) => (
                    <option key={statement.id} value={statement.id}>
                      {statement.statement_number} - {statement.total_amount?.toLocaleString()}원
                    </option>
                  ))}
              </select>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? '매칭 중...' : '매칭'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const CollectionActivityModal = ({ overdue, onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    activity_type: 'phone_call',
    activity_date: new Date().toISOString().slice(0, 16),
    contact_method: 'phone',
    contact_result: 'answered',
    tenant_response: '',
    payment_promise: '',
    promise_amount: '',
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">수금 활동 기록</h3>
          <div className="mb-4 p-3 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-600">
              <strong>임차인:</strong> {overdue.tenant?.name}
            </p>
            <p className="text-sm text-gray-600">
              <strong>연체금액:</strong> {overdue.overdue_amount?.toLocaleString()}원
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">활동 유형</label>
              <select
                value={formData.activity_type}
                onChange={(e) => setFormData({...formData, activity_type: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              >
                <option value="phone_call">전화</option>
                <option value="sms">SMS</option>
                <option value="email">이메일</option>
                <option value="visit">방문</option>
                <option value="legal_notice">법적 통지</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">활동 일시</label>
              <input
                type="datetime-local"
                value={formData.activity_date}
                onChange={(e) => setFormData({...formData, activity_date: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">연락 방법</label>
              <select
                value={formData.contact_method}
                onChange={(e) => setFormData({...formData, contact_method: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="phone">전화</option>
                <option value="sms">SMS</option>
                <option value="email">이메일</option>
                <option value="in_person">대면</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">연락 결과</label>
              <select
                value={formData.contact_result}
                onChange={(e) => setFormData({...formData, contact_result: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="answered">통화됨</option>
                <option value="no_answer">연락 안됨</option>
                <option value="busy">통화중</option>
                <option value="wrong_number">잘못된 번호</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">임차인 응답</label>
              <input
                type="text"
                value={formData.tenant_response}
                onChange={(e) => setFormData({...formData, tenant_response: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">납부 약속일</label>
              <input
                type="date"
                value={formData.payment_promise}
                onChange={(e) => setFormData({...formData, payment_promise: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">약속 금액</label>
              <input
                type="number"
                value={formData.promise_amount}
                onChange={(e) => setFormData({...formData, promise_amount: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">메모</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                rows="3"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? '기록 중...' : '기록'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const StatementPaymentModal = ({ statement, onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'manual',
    reference_number: '',
    notes: '',
    payment_amount: '', // 부분납 금액
    is_partial_payment: false // 부분납 여부
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">청구내역 입금처리</h3>
          <div className="mb-4 p-3 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-600">
              <strong>임차인:</strong> {statement.tenant_name}
            </p>
            <p className="text-sm text-gray-600">
              <strong>청구번호:</strong> {statement.statement_number}
            </p>
            <p className="text-sm text-gray-600">
              <strong>청구금액:</strong> {statement.total_amount?.toLocaleString()}원
            </p>
            <p className="text-sm text-gray-600">
              <strong>납부기한:</strong> {new Date(statement.due_date).toLocaleDateString()}
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 부분납 옵션 */}
            <div className="border border-gray-200 rounded-md p-4 bg-gray-50">
              <div className="flex items-center mb-3">
                <input
                  type="checkbox"
                  id="partial_payment"
                  checked={formData.is_partial_payment}
                  onChange={(e) => {
                    const isPartial = e.target.checked;
                    setFormData({
                      ...formData, 
                      is_partial_payment: isPartial,
                      payment_amount: isPartial ? '' : ''
                    });
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="partial_payment" className="ml-2 text-sm font-medium text-gray-700">
                  부분납 처리
                </label>
              </div>
              
              {formData.is_partial_payment && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    입금 금액
                  </label>
                  <input
                    type="number"
                    value={formData.payment_amount}
                    onChange={(e) => setFormData({...formData, payment_amount: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder={`최대 ${statement.total_amount?.toLocaleString()}원`}
                    min="1"
                    max={statement.total_amount}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    청구금액: {statement.total_amount?.toLocaleString()}원
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">입금일</label>
              <input
                type="date"
                value={formData.payment_date}
                onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">입금 방법</label>
              <select
                value={formData.payment_method}
                onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="manual">수동</option>
                <option value="bank_transfer">계좌이체</option>
                <option value="cash">현금</option>
                <option value="check">수표</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">참조번호</label>
              <input
                type="text"
                value={formData.reference_number}
                onChange={(e) => setFormData({...formData, reference_number: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="거래번호, 수표번호 등"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">메모</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                rows="3"
                placeholder="입금 관련 메모"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? '처리 중...' : (formData.is_partial_payment ? '부분납 처리' : '전액 납부')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// 수납관리 드롭다운 컴포넌트
const PaymentManagementDropdown = ({ item, onAction }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const dropdownHeight = 150; // 드롭다운 예상 높이
      
      // 하단 공간이 부족하고 상단 공간이 충분하면 위로 열기
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setOpenUpward(true);
      } else {
        setOpenUpward(false);
      }
    }
  }, [isOpen]);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getAvailableActions = () => {
    const actions = [];
    
    if (item.payment_status === 'pending') {
      actions.push({ key: 'process', label: '입금처리', icon: CheckCircleIcon, color: 'text-green-600' });
    }
    
    if (item.payment_status === 'partial' || item.payment_status === 'paid') {
      actions.push({ key: 'modify', label: '입금액수정', icon: PencilIcon, color: 'text-blue-600' });
      actions.push({ key: 'cancel', label: '입금취소', icon: XMarkIcon, color: 'text-red-600' });
    }
    
    return actions;
  };

  const availableActions = getAvailableActions();

  if (availableActions.length === 0) {
    return <span className="text-gray-400 text-sm">-</span>;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center px-2 py-0.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        수납관리
        <ChevronDownIcon className="ml-1 h-3 w-3" />
      </button>

      {isOpen && (
        <div 
          className={`absolute right-0 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200 ${
            openUpward ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
        >
          <div className="py-1">
            {availableActions.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.key}
                  onClick={() => {
                    onAction(action.key, item);
                    setIsOpen(false);
                  }}
                  className={`flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${action.color}`}
                >
                  <Icon className="mr-2 h-4 w-4" />
                  {action.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// 수납관리 액션 모달 컴포넌트
const PaymentActionModal = ({ action, onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'manual',
    reference_number: '',
    notes: '',
    amount: action.data?.total_amount || 0,
    payment_id: action.data?.payments?.[0]?.id || null,
    payment_amount: '', // 부분납 금액
    is_partial_payment: false // 부분납 여부
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const getModalTitle = () => {
    switch (action.action) {
      case 'process': return '입금처리';
      case 'modify': return '입금액수정';
      case 'cancel': return '입금취소';
      default: return '수납관리';
    }
  };

  const getModalContent = () => {
    switch (action.action) {
      case 'process':
        return (
          <div className="space-y-4">
            {/* 부분납 옵션 */}
            <div className="border border-gray-200 rounded-md p-4 bg-gray-50">
              <div className="flex items-center mb-3">
                <input
                  type="checkbox"
                  id="partial_payment_action"
                  checked={formData.is_partial_payment}
                  onChange={(e) => {
                    const isPartial = e.target.checked;
                    setFormData({
                      ...formData, 
                      is_partial_payment: isPartial,
                      payment_amount: isPartial ? '' : ''
                    });
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="partial_payment_action" className="ml-2 text-sm font-medium text-gray-700">
                  부분납 처리
                </label>
              </div>
              
              {formData.is_partial_payment && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    입금 금액
                  </label>
                  <input
                    type="number"
                    value={formData.payment_amount}
                    onChange={(e) => setFormData({...formData, payment_amount: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder={`최대 ${action.data?.total_amount?.toLocaleString()}원`}
                    min="1"
                    max={action.data?.total_amount}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    청구금액: {action.data?.total_amount?.toLocaleString()}원
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">입금일</label>
              <input
                type="date"
                value={formData.payment_date}
                onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">입금 방법</label>
              <select
                value={formData.payment_method}
                onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="manual">수동</option>
                <option value="bank_transfer">계좌이체</option>
                <option value="cash">현금</option>
                <option value="check">수표</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">참조번호</label>
              <input
                type="text"
                value={formData.reference_number}
                onChange={(e) => setFormData({...formData, reference_number: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="거래번호, 수표번호 등"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">메모</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                rows="3"
                placeholder="입금 관련 메모"
              />
            </div>
          </div>
        );
      
      case 'modify':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">입금액</label>
              <input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">입금일</label>
              <input
                type="date"
                value={formData.payment_date}
                onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">입금 방법</label>
              <select
                value={formData.payment_method}
                onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="manual">수동</option>
                <option value="bank_transfer">계좌이체</option>
                <option value="cash">현금</option>
                <option value="check">수표</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">참조번호</label>
              <input
                type="text"
                value={formData.reference_number}
                onChange={(e) => setFormData({...formData, reference_number: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="거래번호, 수표번호 등"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">메모</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                rows="3"
                placeholder="입금 관련 메모"
              />
            </div>
          </div>
        );
      
      case 'cancel':
        return (
          <div className="space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">
                <strong>경고:</strong> 입금취소를 하면 해당 입금내역이 삭제되고 청구서 상태가 미납으로 변경됩니다.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">취소 사유</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                rows="3"
                placeholder="입금취소 사유를 입력해주세요"
                required
              />
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{getModalTitle()}</h3>
          
          {/* 청구서 정보 */}
          <div className="mb-4 p-3 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-600">
              <strong>청구서번호:</strong> {action.data?.statement_number}
            </p>
            <p className="text-sm text-gray-600">
              <strong>세입자:</strong> {action.data?.tenant_name}
            </p>
            <p className="text-sm text-gray-600">
              <strong>청구금액:</strong> {action.data?.total_amount?.toLocaleString()}원
            </p>
            {action.data?.total_paid > 0 && (
              <p className="text-sm text-gray-600">
                <strong>기납입액:</strong> {action.data?.total_paid?.toLocaleString()}원
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            {getModalContent()}
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white disabled:opacity-50 ${
                  action.action === 'cancel' 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {loading ? '처리 중...' : 
                 action.action === 'process' ? (formData.is_partial_payment ? '부분납 처리' : '전액 납부') :
                 action.action === 'modify' ? '수정' :
                 action.action === 'cancel' ? '취소' : '확인'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};


const SmsPaymentRegistrationModal = ({ onClose, onSubmit, loading, propertyOwners = [] }) => {
  const [formData, setFormData] = useState({
    tenant_phone: '',
    tenant_name: '',
    amount: '',
    bank_name: '',
    account_number: '',
    sender_name: '',
    property_owner_id: '',
    transaction_time: new Date().toISOString().slice(0, 16),
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">SMS 입금 등록</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            {propertyOwners && propertyOwners.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700">임대사업자</label>
                <select
                  value={formData.property_owner_id}
                  onChange={(e) => setFormData({...formData, property_owner_id: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="">선택하세요</option>
                  {propertyOwners.map((owner) => (
                    <option key={owner.id} value={owner.id}>
                      {owner.company_name || owner.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700">임차인 전화번호</label>
              <input
                type="tel"
                value={formData.tenant_phone}
                onChange={(e) => setFormData({...formData, tenant_phone: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="010-1234-5678"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">임차인 이름</label>
              <input
                type="text"
                value={formData.tenant_name}
                onChange={(e) => setFormData({...formData, tenant_name: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="김철수"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">입금금액</label>
              <input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="500000"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">은행명</label>
              <input
                type="text"
                value={formData.bank_name}
                onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="신한은행"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">입금계좌번호</label>
              <input
                type="text"
                value={formData.account_number}
                onChange={(e) => setFormData({...formData, account_number: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="계좌번호를 입력하세요"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">송금자명</label>
              <input
                type="text"
                value={formData.sender_name}
                onChange={(e) => setFormData({...formData, sender_name: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="김철수"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">거래시간</label>
              <input
                type="datetime-local"
                value={formData.transaction_time}
                onChange={(e) => setFormData({...formData, transaction_time: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">메모</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                rows="3"
                placeholder="추가 정보를 입력하세요"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? '등록 중...' : '등록'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const SmsFormatModal = ({ onClose, smsFormats }) => {
  const [selectedBank, setSelectedBank] = useState(null);

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium text-gray-900">은행별 SMS 입금알림 포맷 가이드</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* 은행 목록 */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-700">은행 선택</h4>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {smsFormats.map((format) => (
                  <button
                    key={format.id}
                    onClick={() => setSelectedBank(format)}
                    className={`w-full text-left p-3 rounded-lg border ${
                      selectedBank?.id === format.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3">
                        <span className="text-white text-sm font-bold">
                          {format.bank_name?.charAt(0) || '?'}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{format.bank_name}</div>
                        <div className="text-sm text-gray-500">{format.bank_code}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* 선택된 은행의 SMS 포맷 상세 */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-700">SMS 포맷 정보</h4>
              {selectedBank ? (
                <div className="space-y-4">
                  {/* SMS 포맷 예시 */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">SMS 포맷 예시</h5>
                    <div className="bg-white p-3 rounded border font-mono text-sm">
                      {selectedBank.sms_format}
                    </div>
                  </div>

                  {/* 파싱 패턴 */}
                  <div className="space-y-3">
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-1">금액 추출 패턴</h5>
                      <div className="bg-white p-2 rounded border font-mono text-xs">
                        {selectedBank.amount_pattern}
                      </div>
                    </div>
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-1">송금자명 추출 패턴</h5>
                      <div className="bg-white p-2 rounded border font-mono text-xs">
                        {selectedBank.sender_pattern}
                      </div>
                    </div>
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-1">계좌번호 추출 패턴</h5>
                      <div className="bg-white p-2 rounded border font-mono text-xs">
                        {selectedBank.account_pattern}
                      </div>
                    </div>
                  </div>

                  {/* 설명 */}
                  {selectedBank.description && (
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <h5 className="text-sm font-medium text-blue-700 mb-1">설명</h5>
                      <p className="text-sm text-blue-600">{selectedBank.description}</p>
                    </div>
                  )}

                  {/* 마지막 업데이트 */}
                  {selectedBank.last_updated && (
                    <div className="text-xs text-gray-500">
                      마지막 업데이트: {new Date(selectedBank.last_updated).toLocaleDateString('ko-KR')}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p>은행을 선택하면 SMS 포맷 정보를 확인할 수 있습니다.</p>
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// SMS 시뮬레이터 모달 컴포넌트
const SmsSimulatorModal = ({ onClose, onSendSms, onTestParse, onGenerateSms, sendLoading, parseLoading, generateLoading, formData, setFormData }) => {
  const [activeTab, setActiveTab] = useState('send');
  const [testMessage, setTestMessage] = useState('');
  const [parseResult, setParseResult] = useState(null);

  // 미리 정의된 SMS 템플릿 (실제 은행 SMS에 가까운 형태)
  const smsTemplates = [
    {
      name: "국민은행 입금알림",
      message: "[국민은행] 12/25 14:30 김철수님께 500,000원이 입금되었습니다. 잔액: 1,234,567원",
      phone: "010-1234-5678"
    },
    {
      name: "신한은행 입금알림", 
      message: "신한은행 12/25 14:30 이영희님께 600,000원이 입금되었습니다. 잔액: 2,345,678원",
      phone: "010-2345-6789"
    },
    {
      name: "우리은행 입금알림",
      message: "우리은행 12/25 14:30 박민수님께 700,000원이 입금되었습니다. 잔액: 3,456,789원",
      phone: "010-3456-7890"
    },
    {
      name: "하나은행 입금알림",
      message: "하나은행 12/25 14:30 최지영님께 800,000원이 입금되었습니다. 잔액: 4,567,890원",
      phone: "010-4567-8901"
    },
    {
      name: "농협은행 입금알림",
      message: "농협 12/25 14:30 정민호님께 450,000원이 입금되었습니다. 잔액: 5,678,901원",
      phone: "010-5678-9012"
    },
    {
      name: "기업은행 입금알림",
      message: "기업은행 12/25 14:30 한소영님께 350,000원이 입금되었습니다. 잔액: 6,789,012원",
      phone: "010-6789-0123"
    }
  ];

  const handleSendSms = (e) => {
    e.preventDefault();
    console.log('SMS 전송 버튼 클릭됨');
    console.log('폼 데이터:', formData);
    
    if (!formData.message.trim()) {
      alert('SMS 메시지를 입력해주세요.');
      return;
    }

    const smsData = {
      message: formData.message,
      phone_number: formData.phone_number || '010-0000-0000',
      received_at: new Date(formData.received_at).toISOString()
    };
    
    console.log('전송할 SMS 데이터:', smsData);
    
    // 먼저 간단한 테스트 API 호출
    fetch('/api/payment/sms/receive-simple', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(smsData)
    })
    .then(response => {
      console.log('간단한 SMS API 응답:', response.status);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return response.json();
    })
    .then(data => {
      console.log('간단한 SMS API 성공:', data);
      // 간단한 API 성공하면 실제 API 호출
      onSendSms(smsData);
    })
    .catch(error => {
      console.error('간단한 SMS API 오류:', error);
      alert('SMS API 연결에 문제가 있습니다: ' + error.message);
    });
  };

  const handleTestParse = () => {
    if (!testMessage.trim()) {
      alert('테스트할 SMS 메시지를 입력해주세요.');
      return;
    }

    onTestParse({ message: testMessage }, {
      onSuccess: (data) => {
        setParseResult(data.data);
      },
      onError: (error) => {
        console.error('파싱 테스트 오류:', error);
        alert('파싱 테스트 중 오류가 발생했습니다.');
      }
    });
  };

  const handleTemplateSelect = (template) => {
    if (activeTab === 'send') {
      setFormData({ 
        ...formData, 
        message: template.message,
        phone_number: template.phone 
      });
    } else {
      setTestMessage(template.message);
    }
  };

  const handleGenerateSms = () => {
    console.log('자동 생성 버튼 클릭됨');
    
    // fetch로 직접 SMS 생성 API 호출
    fetch('/api/payment/sms/generate-test', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({})
    })
    .then(response => {
      console.log('SMS 생성 API 응답:', response.status);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return response.json();
    })
    .then(data => {
      console.log('SMS 생성 성공:', data);
      // 생성된 SMS 데이터를 폼에 자동 입력
      const generatedData = data.data;
      setFormData({
        message: generatedData.message,
        phone_number: generatedData.phone_number,
        received_at: new Date(generatedData.received_at).toISOString().slice(0, 16)
      });
      alert(`SMS 생성 완료!\n${generatedData.tenant_name}님의 ${generatedData.bank_name} 입금 SMS가 생성되었습니다.`);
    })
    .catch(error => {
      console.error('SMS 생성 오류:', error);
      alert('SMS 생성 중 오류가 발생했습니다: ' + error.message);
    });
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">SMS 시뮬레이터</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* 탭 네비게이션 */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('send')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'send'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                SMS 전송
              </button>
              <button
                onClick={() => setActiveTab('parse')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'parse'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                파싱 테스트
              </button>
            </nav>
          </div>

          {/* SMS 전송 탭 */}
          {activeTab === 'send' && (
            <div className="space-y-4">
              {/* 자동 생성 버튼 */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-blue-900">🎲 자동 SMS 생성</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      실제 입금 대상 데이터를 기반으로 랜덤 SMS를 생성합니다
                    </p>
                  </div>
                  <button
                    onClick={handleGenerateSms}
                    disabled={generateLoading}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {generateLoading ? '생성 중...' : '🎲 자동 생성'}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SMS 템플릿 선택
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                  {smsTemplates.map((template, index) => (
                    <button
                      key={index}
                      onClick={() => handleTemplateSelect(template)}
                      className="text-left p-3 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                    >
                      <div className="font-medium">{template.name}</div>
                      <div className="text-gray-600 truncate">{template.message}</div>
                      <div className="text-blue-600 text-xs mt-1">📱 {template.phone}</div>
                    </button>
                  ))}
                </div>
              </div>

              <form onSubmit={handleSendSms} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    SMS 메시지
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    rows={3}
                    placeholder="SMS 메시지를 입력하거나 템플릿을 선택하세요"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      수신 전화번호 (필수)
                    </label>
                    <input
                      type="tel"
                      value={formData.phone_number}
                      onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="010-1234-5678"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">SMS를 받은 전화번호를 입력하세요</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      수신 시간
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.received_at}
                      onChange={(e) => setFormData({ ...formData, received_at: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                    <p className="text-xs text-gray-500 mt-1">SMS를 받은 시간을 입력하세요</p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={sendLoading}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    {sendLoading ? '전송 중...' : 'SMS 전송'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* 파싱 테스트 탭 */}
          {activeTab === 'parse' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  테스트용 SMS 템플릿
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                  {smsTemplates.map((template, index) => (
                    <button
                      key={index}
                      onClick={() => handleTemplateSelect(template)}
                      className="text-left p-3 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                    >
                      <div className="font-medium">{template.name}</div>
                      <div className="text-gray-600 truncate">{template.message}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  테스트할 SMS 메시지
                </label>
                <textarea
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  rows={3}
                  placeholder="파싱을 테스트할 SMS 메시지를 입력하세요"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  닫기
                </button>
                <button
                  onClick={handleTestParse}
                  disabled={parseLoading}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                >
                  {parseLoading ? '테스트 중...' : '파싱 테스트'}
                </button>
              </div>

              {/* 파싱 결과 */}
              {parseResult && (
                <div className="mt-6 p-4 bg-gray-50 rounded-md">
                  <h4 className="font-medium text-gray-900 mb-3">파싱 결과</h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">원본 메시지:</span>
                      <div className="mt-1 p-2 bg-white border rounded text-gray-700">
                        {parseResult.original_message}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="font-medium">은행:</span> {parseResult.parsed_data?.bank_name || 'N/A'}
                      </div>
                      <div>
                        <span className="font-medium">금액:</span> {parseResult.parsed_data?.amount ? `${parseResult.parsed_data.amount.toLocaleString()}원` : 'N/A'}
                      </div>
                      <div>
                        <span className="font-medium">송금자:</span> {parseResult.parsed_data?.sender_name || 'N/A'}
                      </div>
                      <div>
                        <span className="font-medium">거래시간:</span> {parseResult.parsed_data?.transaction_time || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium">파싱 성공:</span> 
                      <span className={`ml-2 ${parseResult.parsed_data?.parsed_successfully ? 'text-green-600' : 'text-red-600'}`}>
                        {parseResult.parsed_data?.parsed_successfully ? '✅ 성공' : '❌ 실패'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// 자동매칭 확인 모달 컴포넌트
const AutoMatchedModal = ({ onClose, onVerifyMatch, onUpdateMatch, verifyLoading, updateLoading, tenants }) => {
  const { data: autoMatchedData, isLoading } = useQuery({
    queryKey: ['auto-matched-sms'],
    queryFn: async () => {
      const response = await api.get('/payment/sms/auto-matched');
      return response.data;
    }
  });

  const [editingSms, setEditingSms] = useState(null);
  const [selectedTenantId, setSelectedTenantId] = useState('');

  const handleVerifyMatch = (smsId, isCorrect) => {
    onVerifyMatch({
      sms_id: smsId,
      is_correct: isCorrect
    });
  };

  const handleUpdateMatch = (smsId) => {
    if (!selectedTenantId) {
      alert('수정할 임차인을 선택해주세요.');
      return;
    }
    
    onUpdateMatch({
      sms_id: smsId,
      tenant_id: parseInt(selectedTenantId)
    });
    
    setEditingSms(null);
    setSelectedTenantId('');
  };

  const handleEditMatch = (sms) => {
    setEditingSms(sms);
    setSelectedTenantId(sms.matched_contract?.id || '');
  };

  const handleCancelEdit = () => {
    setEditingSms(null);
    setSelectedTenantId('');
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-4/5 max-w-6xl shadow-lg rounded-md bg-white">
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">자동매칭된 SMS를 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  const autoMatchedSms = autoMatchedData?.data || [];

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-7xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              자동매칭 확인 ({autoMatchedSms.length}건)
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mt-0.5" />
              <div className="ml-3">
                <h4 className="text-sm font-medium text-yellow-800">
                  자동매칭 정확성 확인 및 수정
                </h4>
                <p className="text-sm text-yellow-700 mt-1">
                  자동매칭된 SMS는 100% 정확하지 않을 수 있습니다. 
                  전화번호, 금액, 송금자명을 확인하여 정확성을 검증하거나, 
                  필요시 "매칭 수정" 버튼으로 올바른 임차인으로 변경할 수 있습니다.
                </p>
              </div>
            </div>
          </div>

          {autoMatchedSms.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <p className="text-gray-600">확인이 필요한 자동매칭 SMS가 없습니다.</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {autoMatchedSms.map((sms) => (
                <div key={sms.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* SMS 정보 */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">SMS 입금내역</h4>
                      <div className="space-y-2 text-sm">
                        <div><span className="font-medium">은행:</span> {sms.bank_name}</div>
                        <div><span className="font-medium">금액:</span> {sms.amount?.toLocaleString()}원</div>
                        <div><span className="font-medium">송금자:</span> {sms.sender_name}</div>
                        <div><span className="font-medium">수신번호:</span> {sms.tenant_phone}</div>
                        <div><span className="font-medium">거래시간:</span> {new Date(sms.transaction_time).toLocaleString()}</div>
                      </div>
                    </div>

                    {/* 매칭된 계약 정보 */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">매칭된 계약</h4>
                      {sms.matched_contract ? (
                        <div className="space-y-2 text-sm">
                          <div><span className="font-medium">임차인:</span> {sms.matched_contract.tenant_name}</div>
                          <div><span className="font-medium">전화번호:</span> {sms.matched_contract.tenant_phone}</div>
                          <div><span className="font-medium">월세:</span> {sms.matched_contract.monthly_rent?.toLocaleString()}원</div>
                          <div><span className="font-medium">건물:</span> {sms.matched_contract.building_name}</div>
                          <div><span className="font-medium">세대:</span> {sms.matched_contract.unit_name}</div>
                          <div><span className="font-medium">납부기한:</span> {new Date(sms.matched_contract.due_date).toLocaleDateString()}</div>
                        </div>
                      ) : (
                        <p className="text-gray-500">매칭된 계약 정보가 없습니다.</p>
                      )}
                    </div>
                  </div>

                  {/* 수정 모드 */}
                  {editingSms?.id === sms.id ? (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                      <h5 className="font-medium text-blue-900 mb-3">매칭 정보 수정</h5>
                      
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          임차인 선택
                        </label>
                        <select
                          value={selectedTenantId}
                          onChange={(e) => setSelectedTenantId(e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                          <option value="">임차인을 선택하세요</option>
                          {tenants.map((tenant) => (
                            <option key={tenant.id} value={tenant.id}>
                              {tenant.name} ({tenant.phone_number}) - {tenant.building_name} {tenant.unit_name} - {tenant.monthly_rent?.toLocaleString()}원
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="flex justify-end space-x-3">
                        <button
                          onClick={handleCancelEdit}
                          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                          취소
                        </button>
                        <button
                          onClick={() => handleUpdateMatch(sms.id)}
                          disabled={updateLoading}
                          className="px-4 py-2 border border-blue-300 rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50"
                        >
                          {updateLoading ? '수정 중...' : '수정 완료'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* 확인 버튼 */
                    <div className="mt-4 flex justify-end space-x-3">
                      <button
                        onClick={() => handleEditMatch(sms)}
                        disabled={verifyLoading || updateLoading}
                        className="px-4 py-2 border border-blue-300 rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50"
                      >
                        ✏️ 매칭 수정
                      </button>
                      <button
                        onClick={() => handleVerifyMatch(sms.id, false)}
                        disabled={verifyLoading || updateLoading}
                        className="px-4 py-2 border border-red-300 rounded-md text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50"
                      >
                        ❌ 매칭 오류
                      </button>
                      <button
                        onClick={() => handleVerifyMatch(sms.id, true)}
                        disabled={verifyLoading || updateLoading}
                        className="px-4 py-2 border border-green-300 rounded-md text-green-700 bg-green-50 hover:bg-green-100 disabled:opacity-50"
                      >
                        ✅ 매칭 정확
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 청구서 확인 모달 컴포넌트
const BillingConfirmationModal = ({ smsId, billingStatement, action, onClose, onConfirm }) => {
  const [notes, setNotes] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm(notes);
  };

  const actionText = action === 'process' ? '입금처리' : action === 'match' ? '매칭' : '매칭취소';
  const actionColor = action === 'process' ? 'bg-green-600 hover:bg-green-700' : action === 'match' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700';

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            청구서 확인 - {actionText}
          </h3>
          
          {billingStatement && (
            <div className="space-y-4">
              {/* 청구서 정보 */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-md font-medium text-gray-700 mb-3">청구서 정보</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-600">청구번호:</span>
                    <span className="ml-2">{billingStatement.statement_number || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">청구월:</span>
                    <span className="ml-2">{billingStatement.billing_month || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">임차인:</span>
                    <span className="ml-2">{billingStatement.tenant?.name || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">건물:</span>
                    <span className="ml-2">{billingStatement.unit?.building?.name || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">호실:</span>
                    <span className="ml-2">{billingStatement.unit?.name || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">납부기한:</span>
                    <span className="ml-2">
                      {billingStatement.due_date ? new Date(billingStatement.due_date).toLocaleDateString('ko-KR') : 'N/A'}
                    </span>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-600">총 청구금액:</span>
                    <span className="text-lg font-bold text-blue-600">
                      {billingStatement.total_amount?.toLocaleString()}원
                    </span>
                  </div>
                </div>
              </div>

              {/* 상세 내역 */}
              {billingStatement.items && billingStatement.items.length > 0 && (
                <div className="bg-white border rounded-lg">
                  <h4 className="text-md font-medium text-gray-700 p-3 border-b">상세 내역</h4>
                  <div className="p-3">
                    <div className="space-y-2">
                      {billingStatement.items.map((item, index) => (
                        <div key={index} className="flex justify-between text-sm">
                          <span>{item.description || item.item_type}</span>
                          <span className="font-medium">{item.amount?.toLocaleString()}원</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* 메모 입력 */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    처리 메모 (선택사항)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                    placeholder={`${actionText} 관련 메모를 입력하세요...`}
                  />
                </div>

                {/* 확인 메시지 */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        <strong>확인:</strong> 위 청구서 정보가 정확한지 확인하세요. 
                        {action === 'process' ? ' 입금처리' : action === 'match' ? ' 매칭' : ' 매칭취소'}는 되돌릴 수 없습니다.
                      </p>
                    </div>
                  </div>
                </div>

                {/* 버튼 */}
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className={`px-4 py-2 border border-transparent rounded-md text-white ${actionColor}`}
                  >
                    {actionText} 실행
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// 청구서 검색 모달 컴포넌트
const BillingSearchModal = ({ smsId, matches, smsRecord, onClose, onSelectBilling }) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              청구서 검색 결과 (SMS ID: {smsId})
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* SMS 정보 */}
          {smsRecord && (
            <div className="bg-blue-50 p-4 rounded-lg mb-4">
              <h4 className="text-md font-medium text-blue-800 mb-2">SMS 입금 정보</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-blue-600">금액:</span>
                  <span className="ml-2">{smsRecord.amount?.toLocaleString()}원</span>
                </div>
                <div>
                  <span className="font-medium text-blue-600">송금자:</span>
                  <span className="ml-2">{smsRecord.sender_name}</span>
                </div>
                <div>
                  <span className="font-medium text-blue-600">거래시간:</span>
                  <span className="ml-2">
                    {smsRecord.transaction_time ? new Date(smsRecord.transaction_time).toLocaleString('ko-KR') : 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-blue-600">수신번호:</span>
                  <span className="ml-2">{smsRecord.tenant_phone}</span>
                </div>
              </div>
            </div>
          )}

          {/* 검색 결과 */}
          <div>
            <h4 className="text-md font-medium text-gray-700 mb-3">
              매칭 가능한 청구서 ({matches?.length || 0}건)
            </h4>
            
            {matches && matches.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {matches.map((match, index) => (
                  <div key={index} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                          <div>
                            <span className="font-medium text-gray-600">청구번호:</span>
                            <span className="ml-2">{match.billing_statement_id || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">청구월:</span>
                            <span className="ml-2">{match.billing_month || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">매칭 점수:</span>
                            <span className="ml-2 font-bold text-blue-600">{match.score}점</span>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                          <div>
                            <span className="font-medium text-gray-600">임차인:</span>
                            <span className="ml-2">{match.tenant_name || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">건물:</span>
                            <span className="ml-2">{match.building_name || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">호실:</span>
                            <span className="ml-2">{match.unit_name || 'N/A'}</span>
                          </div>
                        </div>
                        
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="font-medium text-gray-600">청구금액:</span>
                            <span className="ml-2 text-lg font-bold text-green-600">
                              {match.total_amount ? parseFloat(match.total_amount).toLocaleString() : 'N/A'}원
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">납부기한:</span>
                            <span className="ml-2">
                              {match.due_date ? 
                                new Date(match.due_date).toLocaleDateString('ko-KR') : 'N/A'}
                            </span>
                          </div>
                        </div>
                        
                        {/* 매칭 상세 정보 */}
                        <div className="mt-3 pt-3 border-t text-xs text-gray-500">
                          <div className="space-y-1">
                            <div className="font-medium text-gray-600">매칭 이유:</div>
                            {(match.match_reasons || match.reasons) && (match.match_reasons || match.reasons).length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {(match.match_reasons || match.reasons).map((reason, reasonIndex) => (
                                  <span key={reasonIndex} className="inline-block px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                                    ✅ {reason}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400">매칭 이유 없음</span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="ml-4">
                        <button
                          onClick={() => onSelectBilling({
                            id: match.billing_statement_id,
                            statement_number: match.billing_statement_id,
                            billing_month: match.billing_month,
                            tenant: { name: match.tenant_name },
                            unit: { 
                              building: { name: match.building_name },
                              unit_number: match.unit_name 
                            },
                            total_amount: parseFloat(match.total_amount),
                            due_date: match.due_date
                          })}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          선택
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p>매칭 가능한 청구서가 없습니다.</p>
                <p className="text-sm mt-1">수동으로 청구서를 생성하거나 SMS 데이터를 확인해주세요.</p>
              </div>
            )}
          </div>
          
          {/* 하단 버튼 */}
          <div className="flex justify-end mt-6 pt-4 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// SMS 테스트 탭 렌더링 함수
export default PaymentManagement;
